<template>
  <div class="modal fade" @keyup.esc.prevent="closeModal(false)" tabindex="-1" id="notificationTopicModal" aria-labelledby="notificationTopicModalLabel">
    <div class="modal-dialog modal-lg">
      <div v-if="loaded" class="modal-content shadow-none">
        <loader />
      </div>
      <div v-else class="modal-content shadow-none">
        <div class="modal-header p-5">
          <h5 class="modal-title">{{ topic.id ? 'Топик: ' + (topic.code || '') : 'Новый топик' }}</h5>
          <div @click="closeModal(false)" class="btn btn-icon btn-sm btn-active-light-primary ms-2">
            <i class="ki-duotone ki-cross fs-2x"><span class="path1"></span><span class="path2"></span></i>
          </div>
        </div>
        <div class="modal-body p-5">
          <div class="row">
            <div class="col-md-6">
              <input-field
                v-model="topic.code"
                :required="true"
                :disabled="!!topic.id"
                id="topic.code"
                name="topic.code"
                placeholder="Код (без суффикса окружения)"
                type="text"
                mb="5"
              />
            </div>
            <div class="col-md-3">
              <input-field v-model="topic.sort" id="topic.sort" name="topic.sort" placeholder="Сортировка" type="number" mb="5" />
            </div>
            <div class="col-md-3 d-flex align-items-center mb-5">
              <switch-box-field
                v-model="topic.active"
                id="topic.active"
                name="topic.active"
                placeholder="Активен"
              />
            </div>
          </div>

          <div v-if="topic.fcmTopic" class="mb-5 text-muted fs-7">FCM: {{ topic.fcmTopic }} · Подписчики: {{ topic.subscribers ?? 0 }}</div>

          <input-field v-model="topic.description" id="topic.description" name="topic.description" placeholder="Описание" type="text" mb="5" />

          <div class="fs-6 fw-bold mb-3">Название (name)</div>
          <div class="row">
            <div class="col-md-4">
              <input-field v-model="topic.name['ru-RU']" id="topic.name.ru" name="topic.name.ru" placeholder="Русский (ru-RU)" type="text" mb="5" />
            </div>
            <div class="col-md-4">
              <input-field v-model="topic.name['uz-UZ']" id="topic.name.uz" name="topic.name.uz" placeholder="Узбекский (uz-UZ)" type="text" mb="5" />
            </div>
            <div class="col-md-4">
              <input-field v-model="topic.name['en-US']" id="topic.name.en" name="topic.name.en" placeholder="Английский (en-US)" type="text" mb="5" />
            </div>
          </div>

          <div v-if="topic.id" class="border rounded p-4 mt-2">
            <div class="fs-6 fw-bold mb-2">Подписчики</div>
            <div class="text-muted fs-8 mb-3">CSV или TXT, UTF-8, до 10 МБ. Excel (.xlsx) не поддерживается. Первая строка `user_id` / `id` пропускается.</div>
            <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
              <input
                ref="fileInput"
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                class="form-control form-control-sm w-auto"
                @change="onFileSelected"
              >
              <button
                type="button"
                class="btn btn-sm btn-light-primary"
                :disabled="!selectedFile || uploading"
                @click="uploadSubscribers"
              >
                {{ uploading ? 'Загрузка...' : 'Загрузить файл' }}
              </button>
            </div>

            <div v-if="uploadResult" class="alert alert-light border mb-3 py-3">
              <div>В очереди: {{ uploadResult.queued }} · Дубли: {{ uploadResult.duplicates }} · Уже подписаны: {{ uploadResult.alreadySubscribed }} · Невалидные: {{ uploadResult.invalid }}</div>
              <div v-if="uploadResult.invalidValues && uploadResult.invalidValues.length" class="mt-2 fs-8 text-danger">
                {{ uploadResult.invalidValues.join(', ') }}
              </div>
            </div>

            <div v-if="job" class="mb-3">
              <div class="d-flex justify-content-between fs-8 mb-1">
                <span>{{ jobStatusLabel(job.status) }}</span>
                <span>{{ job.progress ?? 0 }}%</span>
              </div>
              <div class="progress h-20px">
                <div class="progress-bar bg-success" role="progressbar" :style="{ width: (job.progress || 0) + '%' }"></div>
              </div>
            </div>

            <div class="fs-7 fw-bold mb-2">История загрузок</div>
            <div class="table-responsive">
              <table class="table table-sm table-striped fs-8 mb-0">
                <thead>
                <tr class="text-muted">
                  <th>Файл</th>
                  <th>Строк</th>
                  <th>Статус</th>
                  <th>Прогресс</th>
                  <th>Создано</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in jobs" :key="item.id">
                  <td>{{ item.fileName || '—' }}</td>
                  <td>{{ item.rows }}</td>
                  <td>{{ jobStatusLabel(item.status) }}</td>
                  <td>{{ item.progress ?? 0 }}%</td>
                  <td>{{ formatDate(item.createdAt) }}</td>
                </tr>
                <tr v-if="!jobs.length">
                  <td colspan="5" class="text-center text-muted py-4">Загрузок нет</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer p-5 justify-content-between">
          <button type="button" @click="closeModal(false)" class="btn btn-light btn-sm">Закрыть</button>
          <button type="button" @click="saveTopic" :disabled="saving" class="btn btn-primary btn-sm">
            {{ saving ? 'Сохранение...' : (topic.id ? 'Обновить' : 'Создать') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '@/components/Loader'
import InputField from '@/components/Fields/InputField'
import SwitchBoxField from '@/components/Fields/SwitchBoxField'
import { notify } from '@kyvg/vue3-notification'
import axios from 'axios'

const API_BASE = '/notification/api/v2/issuer/topics'

const createLocaleObject = () => ({
  'ru-RU': '',
  'uz-UZ': '',
  'en-US': '',
})

const createDefaultTopic = () => ({
  id: null,
  code: '',
  fcmTopic: '',
  name: createLocaleObject(),
  description: '',
  sort: 0,
  active: true,
  subscribers: 0,
})

export default {
  name: 'NotificationTopicModal',
  components: { Loader, InputField, SwitchBoxField },
  props: {
    itemData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loaded: false,
      saving: false,
      uploading: false,
      topic: createDefaultTopic(),
      selectedFile: null,
      uploadResult: null,
      job: null,
      jobs: [],
      pollTimer: null,
    };
  },
  mounted() {
    this.hydrateTopic();
    if (this.topic.id) this.fetchJobs();
    setTimeout(() => {
      const modalEl = document.getElementById('notificationTopicModal');
      if (modalEl) {
        modalEl.addEventListener('mousedown', (event) => {
          if (event.target.classList.contains('modal')) this.closeModal(false);
        });
      }
    }, 10);
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    getErrorMessage(err) {
      return err?.response?.data?.errorDescription ?? err?.response?.data?.message ?? err.message;
    },
    jobStatusLabel(status) {
      const map = { NEW: 'Ожидание', IN_PROGRESS: 'Обработка', DONE: 'Готово' };
      return map[status] || status || '';
    },
    formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('ru-RU');
    },
    hydrateTopic() {
      const source = this.itemData || {};
      this.topic = {
        ...createDefaultTopic(),
        ...source,
        name: { ...createLocaleObject(), ...(source.name || {}) },
        active: source.active !== false,
      };
    },
    closeModal(isUpdate) {
      this.stopPolling();
      const modalInstance = bootstrap.Modal.getInstance(document.getElementById('notificationTopicModal'));
      if (modalInstance) modalInstance.hide();
      this.$emit('closeModal', isUpdate);
    },
    saveTopic() {
      if (!this.topic.code || !String(this.topic.code).trim()) {
        notify({ duration: 7000, type: 'error', speed: 1000, title: 'Ошибка', text: 'Заполните код топика' });
        return;
      }

      this.saving = true;
      const payload = {
        name: this.topic.name,
        description: this.topic.description ? String(this.topic.description).trim() : '',
        sort: Number(this.topic.sort || 0),
        active: Boolean(this.topic.active),
      };

      const request = this.topic.id
        ? axios.put(`${API_BASE}/${this.topic.id}`, payload)
        : axios.post(API_BASE, { ...payload, code: String(this.topic.code).trim() });

      request
        .then(() => {
          notify({
            duration: 7000,
            type: 'success',
            speed: 1000,
            title: 'Успешно',
            text: this.topic.id ? 'Топик обновлён' : 'Топик создан',
          });
          this.closeModal(true);
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: this.getErrorMessage(err),
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    onFileSelected(event) {
      const file = event?.target?.files?.[0] || null;
      this.selectedFile = file;
    },
    uploadSubscribers() {
      if (!this.topic.id || !this.selectedFile) return;
      if (this.selectedFile.size > 10 * 1024 * 1024) {
        notify({ duration: 7000, type: 'error', speed: 1000, title: 'Ошибка', text: 'Файл больше 10 МБ' });
        return;
      }

      this.uploading = true;
      const form = new FormData();
      form.append('file', this.selectedFile);

      axios.post(`${API_BASE}/${this.topic.id}/subscribers`, form)
        .then((res) => {
          this.uploadResult = res?.data || null;
          const queued = Number(this.uploadResult?.queued || 0);
          const jobId = this.uploadResult?.jobId;
          notify({
            duration: 5000,
            type: 'success',
            speed: 1000,
            title: 'Успешно',
            text: queued ? `В очередь поставлено ${queued}` : 'Нечего подписывать — все уже подписаны или невалидны',
          });
          this.selectedFile = null;
          if (this.$refs.fileInput) this.$refs.fileInput.value = '';
          this.fetchJobs();
          if (queued > 0 && jobId) {
            this.startPolling(jobId);
          } else {
            this.job = {
              status: 'DONE',
              progress: 100,
              pending: 0,
            };
          }
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: this.getErrorMessage(err),
          });
        })
        .finally(() => {
          this.uploading = false;
        });
    },
    fetchJobs() {
      if (!this.topic.id) return;
      axios.get(`${API_BASE}/${this.topic.id}/jobs`, { params: { page: 0, size: 20 } })
        .then((res) => {
          this.jobs = Array.isArray(res?.data?.content) ? res.data.content : [];
        })
        .catch((err) => {
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: this.getErrorMessage(err),
          });
          this.jobs = [];
        });
    },
    startPolling(jobId) {
      this.stopPolling();
      this.pollJob(jobId);
      this.pollTimer = setInterval(() => this.pollJob(jobId), 4000);
    },
    pollJob(jobId) {
      if (!this.topic.id || !jobId) return;
      axios.get(`${API_BASE}/${this.topic.id}/jobs/${jobId}`)
        .then((res) => {
          this.job = res?.data || null;
          if (this.job?.status === 'DONE') {
            this.stopPolling();
            this.fetchJobs();
          }
        })
        .catch((err) => {
          this.stopPolling();
          notify({
            duration: 7000,
            type: 'error',
            speed: 1000,
            title: 'Ошибка',
            text: this.getErrorMessage(err),
          });
        });
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
  },
}
</script>

<style scoped>
</style>
